/**
 * The step list's model: the persisted schema keeps its `section` wrappers
 * (templates, the canonical catalog, the server's normalization and the
 * documents-section file promotion in `flattenIntakeStepNodes` all rely on
 * them), but the client never sees a section, so neither does the editor. The
 * list edits a flat array of steps and these pure functions project it back:
 *
 * - a step moved between two steps lands in the section of the step before it;
 *   a step dragged to the top lands in the first section;
 * - a new question is appended to the last non-documents section (a file
 *   question to the documents section, creating one if absent);
 * - template insertion appends the template's sections whole (questions the
 *   intake already has are skipped);
 * - a section left empty is dropped.
 */

import type {
  OutgoingIntakeFormNode,
  OutgoingIntakeQuestionType
} from "@/api/types/outgoingIntake";
import {
  flattenIntakeStepNodes,
  isDocumentsSectionNode
} from "@/features/foreignNationalOutgoingIntakes/intakeStepFlattening";

export type Step = {
  node: OutgoingIntakeFormNode;
  /** 1-based, as the client counts. */
  number: number;
  /** The section the node lives in, if any (id only). */
  sectionId: string | null;
};

const isSection = (n: OutgoingIntakeFormNode) => n.node_type === "section";

/** Every step with the section it lives under (top-level nodes have none). */
export const stepsOf = (schema: OutgoingIntakeFormNode[]): Step[] => {
  const owner = new Map<OutgoingIntakeFormNode, string | null>();
  const visit = (nodes: OutgoingIntakeFormNode[], sectionId: string | null) => {
    for (const node of nodes) {
      if (isSection(node)) {
        visit(node.children ?? [], node.node_id);
      } else {
        owner.set(node, sectionId);
      }
    }
  };
  visit(schema, null);
  return flattenIntakeStepNodes(schema).map((node, i) => ({
    node,
    number: i + 1,
    sectionId: owner.get(node) ?? null
  }));
};

export const stepLabel = (node: OutgoingIntakeFormNode): string =>
  (node.prompt ?? node.title ?? node.node_id ?? "").toString().trim() ||
  "Untitled";

const cloneTree = (nodes: OutgoingIntakeFormNode[]): OutgoingIntakeFormNode[] =>
  nodes.map((n) => ({
    ...n,
    children: n.children ? cloneTree(n.children) : n.children
  }));

/** `(siblings, index)` of `nodeId` anywhere in the tree, or null. */
export const locate = (
  nodes: OutgoingIntakeFormNode[],
  nodeId: string
): {
  siblings: OutgoingIntakeFormNode[];
  index: number;
  parent: OutgoingIntakeFormNode | null;
} | null => {
  const walk = (
    list: OutgoingIntakeFormNode[],
    parent: OutgoingIntakeFormNode | null
  ): {
    siblings: OutgoingIntakeFormNode[];
    index: number;
    parent: OutgoingIntakeFormNode | null;
  } | null => {
    for (let i = 0; i < list.length; i += 1) {
      if (list[i].node_id === nodeId)
        return { siblings: list, index: i, parent };
      const found = walk(list[i].children ?? [], list[i]);
      if (found) return found;
    }
    return null;
  };
  return walk(nodes, null);
};

export const findNode = (
  nodes: OutgoingIntakeFormNode[],
  nodeId: string
): OutgoingIntakeFormNode | null =>
  locate(nodes, nodeId)?.siblings[locate(nodes, nodeId)!.index] ?? null;

const dropEmptySections = (
  nodes: OutgoingIntakeFormNode[]
): OutgoingIntakeFormNode[] =>
  nodes.filter((n) => !isSection(n) || (n.children?.length ?? 0) > 0);

const renumber = (nodes: OutgoingIntakeFormNode[]): OutgoingIntakeFormNode[] =>
  nodes.map((n, i) => ({
    ...n,
    order: i + 1,
    children: n.children ? renumber(n.children) : n.children
  }));

export const finalize = (
  nodes: OutgoingIntakeFormNode[]
): OutgoingIntakeFormNode[] => renumber(dropEmptySections(nodes));

/** Replace a node in place (by id) with `patch` merged over it. */
export const updateNode = (
  schema: OutgoingIntakeFormNode[],
  nodeId: string,
  patch: Partial<OutgoingIntakeFormNode>
): OutgoingIntakeFormNode[] => {
  const next = cloneTree(schema);
  const hit = locate(next, nodeId);
  if (!hit) return schema;
  hit.siblings[hit.index] = { ...hit.siblings[hit.index], ...patch };
  return next;
};

export const removeNode = (
  schema: OutgoingIntakeFormNode[],
  nodeId: string
): OutgoingIntakeFormNode[] => {
  const next = cloneTree(schema);
  const hit = locate(next, nodeId);
  if (!hit) return schema;
  hit.siblings.splice(hit.index, 1);
  return finalize(next);
};

/**
 * Move a step so it sits after `afterNodeId` (or first when null). The step
 * joins the section of the step it now follows; moved to the top it joins the
 * first section, or the top level when there are no sections.
 */
export const moveStep = (
  schema: OutgoingIntakeFormNode[],
  nodeId: string,
  afterNodeId: string | null
): OutgoingIntakeFormNode[] => {
  if (nodeId === afterNodeId) return schema;
  const next = cloneTree(schema);
  const from = locate(next, nodeId);
  if (!from) return schema;
  const [moving] = from.siblings.splice(from.index, 1);
  if (afterNodeId) {
    const to = locate(next, afterNodeId);
    if (!to) return schema;
    to.siblings.splice(to.index + 1, 0, moving);
  } else {
    const firstSection = next.find(isSection);
    if (firstSection) (firstSection.children ??= []).unshift(moving);
    else next.unshift(moving);
  }
  return finalize(next);
};

const uniqueId = (schema: OutgoingIntakeFormNode[], base: string): string => {
  let id = base;
  let n = 2;
  while (locate(schema, id)) {
    id = `${base}_${n}`;
    n += 1;
  }
  return id;
};

export const slugify = (value: string): string =>
  (
    value
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "_")
      .replace(/^_+|_+$/g, "") || "question"
  ).slice(0, 60);

export type NewQuestion = {
  prompt: string;
  question_type: OutgoingIntakeQuestionType;
  required?: boolean;
  help_text?: string | null;
  options?: { option_id: string; label: string }[];
};

/**
 * Append a custom question: uploads go with the other uploads (a documents
 * section, created when missing); everything else after the last
 * non-documents section's steps, so the client's order stays sane. A flat
 * schema (no sections) just gets it at the end. With `afterNodeId` it goes
 * right after that step instead, inside the step's section; after a
 * top-level step it opens the section that follows, when one does.
 */
export const addCustomQuestion = (
  schema: OutgoingIntakeFormNode[],
  question: NewQuestion,
  afterNodeId?: string | null
): { schema: OutgoingIntakeFormNode[]; nodeId: string } => {
  const next = cloneTree(schema);
  const nodeId = uniqueId(next, `custom.${slugify(question.prompt)}`);
  const node: OutgoingIntakeFormNode = {
    node_id: nodeId,
    node_type: "question",
    prompt: question.prompt,
    question_type: question.question_type,
    required: Boolean(question.required),
    help_text: question.help_text ?? null,
    options: question.options ?? [],
    model_path: null,
    template_source: "custom",
    children: []
  };
  if (afterNodeId) {
    const to = locate(next, afterNodeId);
    if (to) {
      const following = to.siblings[to.index + 1];
      if (
        !to.parent &&
        following &&
        isSection(following) &&
        isDocumentsSectionNode(following) === (node.question_type === "file")
      )
        (following.children ??= []).unshift(node);
      else to.siblings.splice(to.index + 1, 0, node);
      return { schema: finalize(next), nodeId };
    }
  }
  if (!next.some(isSection)) {
    next.push(node);
    return { schema: finalize(next), nodeId };
  }
  const isFile = question.question_type === "file";
  let target: OutgoingIntakeFormNode | undefined;
  for (const section of next) {
    if (isSection(section) && isDocumentsSectionNode(section) === isFile)
      target = section;
  }
  if (!target) {
    target = {
      node_id: isFile
        ? uniqueId(next, "documents")
        : uniqueId(next, "custom.questions"),
      node_type: "section",
      title: isFile ? "Documents" : "Additional questions",
      children: []
    };
    if (isFile) next.push(target);
    else {
      // Before the documents section, if there is one, so uploads stay last.
      const docsIndex = next.findIndex(
        (n) => isSection(n) && isDocumentsSectionNode(n)
      );
      if (docsIndex >= 0) next.splice(docsIndex, 0, target);
      else next.push(target);
    }
  }
  (target.children ??= []).push(node);
  return { schema: finalize(next), nodeId };
};

/** A new repeating step (empty group) after `afterNodeId` or at the end of the last section. */
export const addRepeatingGroup = (
  schema: OutgoingIntakeFormNode[],
  title: string,
  afterNodeId?: string | null
): { schema: OutgoingIntakeFormNode[]; nodeId: string } => {
  const next = cloneTree(schema);
  const nodeId = uniqueId(next, `custom.${slugify(title)}_group`);
  const node: OutgoingIntakeFormNode = {
    node_id: nodeId,
    node_type: "repeatable_group",
    title,
    children: [],
    min_items: 1,
    max_items: null
  };
  const to = afterNodeId ? locate(next, afterNodeId) : null;
  if (to) to.siblings.splice(to.index + 1, 0, node);
  else {
    const last = [...next]
      .reverse()
      .find((n) => isSection(n) && !isDocumentsSectionNode(n));
    if (last) (last.children ??= []).push(node);
    else next.push(node);
  }
  return { schema: renumber(next), nodeId };
};

const questionIds = (nodes: OutgoingIntakeFormNode[]): Set<string> => {
  const ids = new Set<string>();
  const walk = (list: OutgoingIntakeFormNode[]) => {
    for (const n of list) {
      if (n.node_type === "question") ids.add(n.node_id);
      if (n.children) walk(n.children);
    }
  };
  walk(nodes);
  return ids;
};

/**
 * Append a template's (or any) nodes: sections whole, skipping questions the
 * intake already has. Returns how many steps were added.
 */
export const addNodes = (
  schema: OutgoingIntakeFormNode[],
  incoming: OutgoingIntakeFormNode[]
): { schema: OutgoingIntakeFormNode[]; added: number } => {
  const existing = questionIds(schema);
  const prune = (nodes: OutgoingIntakeFormNode[]): OutgoingIntakeFormNode[] => {
    const kept: OutgoingIntakeFormNode[] = [];
    for (const node of cloneTree(nodes)) {
      if (node.node_type === "question") {
        if (!existing.has(node.node_id)) kept.push(node);
      } else {
        node.children = prune(node.children ?? []);
        if (node.children.length || node.node_type === "repeatable_group")
          kept.push(node);
      }
    }
    return kept;
  };
  const pruned = prune(incoming);
  const before = flattenIntakeStepNodes(schema).length;
  const merged = finalize([...cloneTree(schema), ...pruned]);
  return {
    schema: merged,
    added: flattenIntakeStepNodes(merged).length - before
  };
};

/** Duplicate a step right after itself with a fresh id and "(copy)" suffix. */
export const duplicateStep = (
  schema: OutgoingIntakeFormNode[],
  nodeId: string
): OutgoingIntakeFormNode[] => {
  const next = cloneTree(schema);
  const hit = locate(next, nodeId);
  if (!hit) return schema;
  const source = hit.siblings[hit.index];
  const copy: OutgoingIntakeFormNode = {
    ...cloneTree([source])[0],
    node_id: uniqueId(next, `custom.${slugify(stepLabel(source))}`),
    model_path: null,
    template_source: "custom",
    prompt: source.prompt ? `${source.prompt} (copy)` : source.prompt,
    title: source.title ? `${source.title} (copy)` : source.title
  };
  hit.siblings.splice(hit.index + 1, 0, copy);
  return renumber(next);
};
